import { Form, Checkbox, message } from 'antd';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../../../app/hooks';
import { changeToken } from '../../../app/store/authStore';
import { Button } from '../../../components/antd/Button';
import { FormItemOTP } from '../../../components/antd/formItems/FormItemOTP';
import { LayoutLogin } from '../../../components/common/layoutLogin/LayoutLogin';
import {
  useVerifyCodeMutation,
  useResendCodeMutation,
} from '../../../services/twoFactorApi';
import {
  VerifyContainer,
  Title,
  Description,
  ButtonContainer,
  LinkButton,
} from './styles';

export function Verify2FA() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  // Tenta obter do location.state primeiro, depois do sessionStorage
  const getChallenge = (): {
    challenge_id?: string;
    user_id?: string;
    trusted_devices_enabled?: boolean;
  } => {
    const stateData = location.state as {
      challenge_id?: string;
      user_id?: string;
      trusted_devices_enabled?: boolean;
    };

    if (stateData?.challenge_id && stateData?.user_id) {
      return stateData;
    }

    // Fallback para sessionStorage (caso a página seja recarregada)
    const stored = sessionStorage.getItem('2fa_challenge');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return {};
      }
    }

    return {};
  };

  const {
    challenge_id,
    user_id,
    trusted_devices_enabled = true,
  } = getChallenge();

  const [verifyCode, { isLoading: isVerifying }] = useVerifyCodeMutation();
  const [resendCode, { isLoading: isResending }] = useResendCodeMutation();

  if (!challenge_id || !user_id) {
    navigate('/login');
    return null;
  }

  const onFinish = async (values: { code: string; trust_device?: boolean }) => {
    try {
      const result = await verifyCode({
        challenge_id,
        code: values.code,
        trust_device: values.trust_device || false,
      }).unwrap();

      // Limpa o sessionStorage após sucesso
      sessionStorage.removeItem('2fa_challenge');
      dispatch(changeToken(result));
      message.success('Login realizado com sucesso!');
      navigate('/');
    } catch (error: any) {
      message.error(error?.data?.error || 'Código inválido ou expirado');
      form.resetFields(['code']);
    }
  };

  const handleResendCode = async () => {
    try {
      const result = await resendCode({ challenge_id }).unwrap();
      message.success(result.message || 'Novo código enviado!');
    } catch (error: any) {
      message.error(error?.data?.error || 'Erro ao reenviar código');
    }
  };

  return (
    <LayoutLogin>
      <VerifyContainer>
        <Title>Verificação em Dois Fatores</Title>
        <Description>
          Digite o código de 6 dígitos enviado para seu email
        </Description>

        <Form form={form} onFinish={onFinish} layout="vertical">
          <FormItemOTP
            name="code"
            length={6}
            required
            disabled={isVerifying}
            autoSubmit={false}
          />

          {trusted_devices_enabled && (
            <Form.Item
              name="trust_device"
              valuePropName="checked"
              style={{ textAlign: 'center' }}
            >
              <Checkbox>
                Confiar neste dispositivo por{' '}
                {process.env.REACT_APP_TWO_FACTOR_TRUSTED_DEVICE_EXPIRY_DAYS ||
                  30}{' '}
                dias
              </Checkbox>
            </Form.Item>
          )}

          <ButtonContainer>
            <Button
              type="primary"
              htmlType="submit"
              loading={isVerifying}
              disabled={isResending}
              style={{ width: '100%', height: '45px', fontSize: '16px' }}
            >
              Verificar
            </Button>
          </ButtonContainer>

          <ButtonContainer>
            <LinkButton
              type="link"
              onClick={handleResendCode}
              loading={isResending}
              disabled={isVerifying}
            >
              Reenviar código
            </LinkButton>

            <LinkButton
              type="link"
              onClick={() => {
                sessionStorage.removeItem('2fa_challenge');
                navigate('/login');
              }}
              disabled={isVerifying || isResending}
            >
              Voltar ao login
            </LinkButton>
          </ButtonContainer>
        </Form>
      </VerifyContainer>
    </LayoutLogin>
  );
}

export default Verify2FA;
